import React, { ReactNode, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { ActionIcon } from './ActionIcon';
import { config, Icons } from '../index';

export interface CarouselProps {
  /* Carousel node props */
  nodes?: ReactNode[];
  /* Carousel links props */
  links?: LinkProps[];
  /* Carousel like icon state */
  saved?: boolean; // notify via liked icon indicator when true
  /* Carousel max width */
  maxWidth?: string;
  /* Carousel height */
  height?: string;
  /* Carousel margin */
  margin?: string;
  /* Carousel padding */
  padding?: string;
  /* Carousel border radius */
  borderRadius?: string;
  /* Carousel background color */
  backgroundColor?: string;
  /* Carousel border */
  border?: string;
  /* Carousel additional class props */
  className?: string;
  /* Carousel loop state | loop content on timeout */
  isLoop?: boolean;
  /* Carousel timeout in milliseconds */
  timeout?: number;
  /* Carousel onClick handler */
  onClick?: (event?: React.MouseEvent) => void;
  /* Carousel onLikeClick handler */
  onLikeClick?: (event?: React.MouseEvent) => void;
}

export interface LinkProps {
  url?: string;
  title?: StringProps;
  body?: StringProps;
  onClick?: (event?: React.MouseEvent) => void;
}
export interface ImageProps extends CarouselProps {
  width?: string;
  isForwardSlide?: boolean;
  isCurrentSlide?: boolean;
  opacity?: number;
}

export interface CardIconProps extends CarouselProps {
  left?: string;
  right?: string;
}

const StyledWrapper = styled.div<CarouselProps>`
  display: flex;
  position: relative;
  margin: auto;

  height: ${(props) => props.height};
  width: -webkit-fill-available;
  max-width: ${(props) => props.maxWidth};
  background-color: ${(props) => props.backgroundColor};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
`;

const StyledCarouselWrapper = styled.div<CarouselProps>`
  display: flex;
  position: relative;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  overflow: hidden;

  height: ${(props) => props.height};
  margin: ${(props) => props.margin};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
  background-color: ${(props) => props.backgroundColor};
  cursor: pointer;
  user-select: none;
  transition: ${config.transitionShort};

  &:hover {
    background-color: ${(props) => props.backgroundColor};
  }
  &:active {
    background-color: ${(props) => props.backgroundColor};
  }
`;

const StyledMarkersWrapper = styled.div<CarouselProps>`
  display: flex;
  gap: ${config.spacingSmall};
  position: absolute;
  z-index: 3;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  margin: 0 0 ${config.spacingSmall};
`;

const StyledMarkers = styled.div<ImageProps>`
  width: ${config.spacingSmall};
  height: ${config.spacingSmall};
  transition: ${config.transitionShort};
  background-color: ${(props) =>
    props.isCurrentSlide ? config.darkMuted : props.backgroundColor};
  border-radius: 50%;
`;

const StyledSlides = styled.div<CarouselProps>`
  position: relative;
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const StilledImage = styled.img<ImageProps>`
  position: absolute;
  z-index: 1;
  transition: ${config.transitionMedium};
  opacity: ${(props) => props.opacity};
  width: ${(props) => props.width};
  height: 100%;

  object-fit: cover;
  object-position: center;
  cursor: pointer;
  user-select: none;
`;

const StilledNode = styled.div<ImageProps>`
  position: absolute;
  z-index: 1;
  transition: ${config.transitionMedium};
  opacity: ${(props) => props.opacity};
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  user-select: none;
`;

const StyledIconWrapper = styled.div<CardIconProps>`
  position: absolute;
  z-index: 3;
  top: 50%;
  transition: ${config.transitionShort};
  opacity: 0;

  left: ${(props) => props.left ?? undefined};
  right: ${(props) => props.right ?? undefined};

  &:hover {
    opacity: 0.5;
  }

  ${StyledCarouselWrapper}:hover & {
    opacity: 1;
  }
`;

const StyledLikeIconWrapper = styled.div<CardIconProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 99;
  top: ${config.spacingXXLarge};
  right: ${config.spacingSmall};
  transform: translate(-50%, -50%);
  transition: ${config.transitionMedium};
  overflow: hidden;
  background-color: ${(props) =>
    props.saved ? config.cedarChest : config.muted};

  width: ${config.spacingXXLarge};
  height: ${config.spacingXXLarge};
  border-radius: 50%;
  background: ${(props) => props.backgroundColor};
  cursor: pointer;

  &:hover {
    opacity: 0.5;
    background-color: ${config.cedarChest};
  }

  ${StyledCarouselWrapper}:hover & {
    opacity: 1;
  }
`;

const StyledTitle = styled.div<CarouselProps>`
  position: absolute;
  z-index: 4;
  top: ${config.spacingLarge};
  left: 50%;
  transform: translateX(-50%);
`;

const IconWrapper = styled.div<CarouselProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const StyledBody = styled.div<CarouselProps>`
  position: absolute;
  z-index: 5;
  bottom: ${config.spacingXXXLarge};
  left: 50%;
  transform: translateX(-50%);
`;

// 📌 Primary UI component for user interaction
export const Carousel: React.FC<CarouselProps> = ({
  nodes,
  links,
  saved = false,
  // --------------------------------------------------------------------------------
  maxWidth,
  height = '400px',
  border = `1px solid ${config.muted}`,
  borderRadius = config.spacingXMedium,
  margin,
  padding,
  backgroundColor,
  isLoop = false,
  timeout = 3000,
  // --------------------------------------------------------------------------------
  onClick,
  onLikeClick,
  // additional props
  ...props
}) => {
  const [nodeSlides, setNodeSlides] = useState<ReactNode[]>();
  const [slides, setSlides] = useState<LinkProps[]>();
  const [idx, setIdx] = useState(0);
  const [loop, setLoop] = useState(0);

  // --------------------------------------------------------------------------------
  // 📌  Define blob length of total slides
  // --------------------------------------------------------------------------------
  let SLIDE_LENGTH = slides?.length;
  if (nodeSlides) SLIDE_LENGTH = nodeSlides.length;

  // track carousel direction change type === 'next' | 'prev'
  const directionRef = useRef('next');

  // --------------------------------------------------------------------------------
  const handleBackClick = () => {
    // decrement idx handler 1 & if idx negative then set to slides.length - 1
    setIdx(idx - 1 < 0 ? (SLIDE_LENGTH as number) - 1 : idx - 1);
    // set slide defection ref to 'prev'
    directionRef.current = 'prev';
  };

  const handleForwardClick = () => {
    // increment idx by 1 for indxArray state & if slides.length is greater than idx, set idx to 0
    setIdx((idx) => (idx + 1) % (SLIDE_LENGTH as number));
    // set slide defection ref to 'next'
    directionRef.current = 'next';
  };

  const handleSlideClick = () => {
    onClick?.();
    // set to next slide
    handleForwardClick();
  };

  useEffect(() => {
    // --------------------------------------------------------------------------------
    // 📌  Set carousel slide items to default
    // --------------------------------------------------------------------------------
    if (nodes) {
      setNodeSlides(nodes as ReactNode[]);
      return;
    }

    setSlides(links as LinkProps[]);
  }, [nodes, links]);

  // set idx increment on timeout for all nodes
  useEffect(() => {
    // --------------------------------------------------------------------------------
    // 📌  Set carousel loop on timeout
    // --------------------------------------------------------------------------------
    if (!isLoop) return;

    setTimeout(() => {
      setLoop((loop) => loop + 1);
      // alow slides to loop on timeout
      // allow slides to be set to loop on timeout
      if (slides) handleForwardClick();
    }, timeout);

    // 👇️ disable the rule for a single line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loop]);

  return (
    <StyledWrapper
      backgroundColor={backgroundColor}
      margin={margin}
      padding={padding}
      height={height}
      maxWidth={maxWidth}
      // additional props
      {...props}
    >
      <StyledCarouselWrapper border={border} borderRadius={borderRadius}>
        {!SLIDE_LENGTH && (
          <IconWrapper onClick={onClick}>
            <Icons icon="select-camera" size={160} onClick={() => {}} />
          </IconWrapper>
        )}
        {!!SLIDE_LENGTH && SLIDE_LENGTH > 1 && (
          <>
            <StyledIconWrapper left="20px">
              <ActionIcon
                icon="chevron-left"
                backgroundColor={nodeSlides ? config.muted : config.white}
                fill={saved ? config.red : config.grey}
                onClick={handleBackClick}
              />
            </StyledIconWrapper>
            <StyledIconWrapper right="20px">
              <ActionIcon
                icon="chevron-right"
                backgroundColor={nodeSlides ? config.muted : config.white}
                fill={saved ? config.red : config.grey}
                onClick={handleForwardClick}
              />
            </StyledIconWrapper>
          </>
        )}

        <StyledLikeIconWrapper saved={saved} onClick={onLikeClick}>
          <Icons
            icon="hart"
            fill={saved ? config.white : config.red}
            onClick={() => {}}
          />
        </StyledLikeIconWrapper>

        {slides &&
          slides.map(({ url, title, body, onClick }, key) => {
            let isCurrentSlide = key === idx;
            let isForwardSlide = directionRef.current === 'next';

            return (
              <StyledSlides
                key={key}
                borderRadius={borderRadius}
                onClick={() => {
                  // onClick event passed through props
                  onClick?.();
                  handleSlideClick();
                }}
              >
                {title && (
                  <StyledTitle>
                    <String
                      fontSize={`${config.fontMedium}`}
                      color={config.white}
                      align="center"
                      opacity={0.8}
                      limit={1}
                      {...title}
                    />
                  </StyledTitle>
                )}

                {body && (
                  <StyledBody>
                    <String
                      fontSize={`${config.fontMedium}`}
                      color={config.white}
                      align="center"
                      opacity={0.6}
                      limit={4}
                      {...body}
                    />
                  </StyledBody>
                )}

                <StilledImage
                  src={url as string} // default to url as string if no image is passed
                  alt={title?.string ?? "Image can't be loaded"}
                  width="100%"
                  opacity={isCurrentSlide ? 1 : 0}
                  isForwardSlide={isForwardSlide}
                  loading={isCurrentSlide ? 'eager' : 'lazy'}
                />
              </StyledSlides>
            );
          })}

        {nodeSlides &&
          nodeSlides.map((node, key) => {
            let isCurrentSlide = key === idx;
            let isForwardSlide = directionRef.current === 'next';

            return (
              <StyledSlides
                key={key}
                onClick={() => {
                  // onClick event passed through props
                  onClick && onClick();
                  handleSlideClick();
                }}
              >
                <StilledNode
                  opacity={isCurrentSlide ? 1 : 0}
                  isForwardSlide={isForwardSlide}
                >
                  {node}
                </StilledNode>
              </StyledSlides>
            );
          })}

        {!!SLIDE_LENGTH && SLIDE_LENGTH > 1 && (
          <StyledMarkersWrapper>
            {Array(SLIDE_LENGTH)
              .fill('_')
              .map((_, key) => {
                let isCurrentSlide = key === idx;

                return (
                  <StyledMarkers
                    key={key}
                    backgroundColor={nodeSlides ? config.muted : config.white}
                    isCurrentSlide={isCurrentSlide}
                    onClick={() => {
                      // set idx to key
                      setIdx(key);
                    }}
                  />
                );
              })}
          </StyledMarkersWrapper>
        )}
      </StyledCarouselWrapper>
    </StyledWrapper>
  );
};
